import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Send } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

// Using centralized api

function Messages() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [showNewChat, setShowNewChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchConversations();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      fetchMessages(selectedUser.user_id);
      const interval = setInterval(() => fetchMessages(selectedUser.user_id), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUser]);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await api.get('/users');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (otherUserId) => {
    try {
      const response = await api.get(`/messages/${otherUserId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    setSending(true);
    try {
      await api.post(
        '/messages',
        { to_user_id: selectedUser.user_id, content: newMessage }
      );
      setNewMessage('');
      fetchMessages(selectedUser.user_id);
      fetchConversations();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = (user) => {
    setSelectedUser(user);
    setShowNewChat(false);
    setMessages([]);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto h-[calc(100vh-12rem)]" data-testid="messages-page">
        <div className="mb-6">
          <h1 className="text-4xl font-bold tracking-tight mb-2">Mensajes</h1>
          <p className="text-muted-foreground">Comunícate con tu agencia y otros jugadores</p>
        </div>

        <div className="grid grid-cols-12 gap-6 h-full">
          {/* Conversations List */}
          <Card className="col-span-12 lg:col-span-4 bento-card flex flex-col">
            <div className="p-4 border-b border-border">
              <Button
                onClick={() => setShowNewChat(!showNewChat)}
                className="w-full rounded-full"
                data-testid="new-chat-button"
              >
                Nuevo Chat
              </Button>
            </div>

            <ScrollArea className="flex-1">
              {showNewChat ? (
                <div className="p-4 space-y-2">
                  <p className="text-sm font-medium mb-3">Selecciona un usuario:</p>
                  {allUsers.map((u) => (
                    <div
                      key={u.user_id}
                      onClick={() => handleStartNewChat(u)}
                      className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary cursor-pointer transition-colors"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={u.picture} />
                        <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{u.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-2">
                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : conversations.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-8">
                      No hay conversaciones
                    </p>
                  ) : (
                    conversations.map((conv) => (
                      <div
                        key={conv.user_id}
                        onClick={() => setSelectedUser(conv)}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedUser?.user_id === conv.user_id
                            ? 'bg-accent text-accent-foreground'
                            : 'hover:bg-secondary'
                        }`}
                      >
                        <Avatar className="h-10 w-10">
                          <AvatarFallback>{conv.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{conv.name}</p>
                          <p className="text-xs truncate opacity-75">{conv.last_message}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </ScrollArea>
          </Card>

          {/* Messages Area */}
          <Card className="col-span-12 lg:col-span-8 bento-card flex flex-col">
            {selectedUser ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border flex items-center space-x-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{selectedUser.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{selectedUser.name}</p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg.message_id}
                        className={`flex ${
                          msg.from_user_id === user.user_id ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`message-bubble rounded-2xl px-4 py-2 ${
                            msg.from_user_id === user.user_id
                              ? 'bg-accent text-accent-foreground'
                              : 'bg-secondary text-secondary-foreground'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p className="text-xs opacity-60 mt-1">
                            {new Date(msg.timestamp).toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-4 border-t border-border">
                  <form onSubmit={handleSendMessage} className="flex space-x-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Escribe un mensaje..."
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-button">
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <p className="text-muted-foreground">Selecciona una conversación o inicia un nuevo chat</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default Messages;